import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Bell, RefreshCw } from 'lucide-react';

import { API_URL } from '../config';
const API = API_URL;

const AdminReminders = ({ user, onLogout }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/reminder-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data.reminders);
    } catch (error) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const sendRemindersNow = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/send-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sent ${response.data.reminders_sent} reminder(s)`);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <header
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{ background: 'rgba(0, 0, 0, 0.8)', borderBottom: '1px solid #2A2A2A' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => window.location.href = '/admin'}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-black" style={{ color: '#EE1C4E' }}>
              Reminder History
            </h1>
          </div>

          <Button
            onClick={sendRemindersNow}
            disabled={sending}
            className="h-10 px-6 text-white font-bold rounded-full flex items-center gap-2"
            style={{ background: '#EE1C4E' }}
          >
            <RefreshCw className={`w-4 h-4 ${sending ? 'animate-spin' : ''}`} />
            {sending ? 'Sending...' : 'Send Now'}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <p className="text-gray-500">No reminders sent yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reminders.map((reminder, index) => (
              <div
                key={index}
                className="p-4 rounded-xl flex items-center justify-between"
                style={{ background: '#121212', border: '1px solid #2A2A2A' }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold">{reminder.user_email}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: '#EE1C4E', color: 'white' }}>
                      {reminder.days_remaining} days left
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">{reminder.user_name}</p>
                  <p className="text-gray-600 text-xs mt-1">{reminder.message}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs">Sent</p>
                  <p className="text-white text-sm">{formatDate(reminder.reminder_date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminReminders;